import { createRoot } from 'react-dom/client';
import { PersistGate } from 'redux-persist/integration/react';
import { router } from './router';
import { RouterProvider } from 'react-router';
import Tracker from '@openreplay/tracker';
import { useEffect } from 'react';
import { Provider, useSelector } from 'react-redux';
import store, { persistor, RootState } from './redux/store';

import './index.css';

declare global {
  interface Window {
    $crisp: unknown[];
    CRISP_WEBSITE_ID: string;
  }
}

const RootComponent = () => {
  const { token, currentUser } = useSelector((state: RootState) => state.app);

  const initializeOpenReplay = () => {
    const tracker = new Tracker({
      projectKey: process.env.REACT_APP_OPEN_REPLAY_KEY,
      network: {
        capturePayload: true,
        sessionTokenHeader: '',
        failuresOnly: false,
        ignoreHeaders: [],
        captureInIframes: false
      },
      obscureTextEmails: false,
      obscureInputEmails: false,
      defaultInputMode: 0
    });
    if (currentUser?.email) {
      tracker.setUserID(currentUser?.email as string);
    }
    tracker.start();
  };

  const initializeCrisp = () => {
    if (window.$crisp) {
      window.$crisp.push(['set', 'user:email', [currentUser?.email]]);
      window.$crisp.push(['do', 'chat:show']);
    } else {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = process.env.REACT_APP_CRISP_WEBSITE_ID || '';
      const d = document;
      const s = d.createElement('script');
      s.src = 'https://client.crisp.chat/l.js';
      s.async = true;
      s.onload = () => {
        window.$crisp.push(['set', 'user:email', [currentUser?.email]]);
        window.$crisp.push(['do', 'chat:show']);
      };
      d.getElementsByTagName('head')[0].appendChild(s);
    }
  };

  const initializeCdnBug = () => {
    // if script is already loaded, do not load it again
    if (document.querySelector('script[src="https://cdn.bugdrop.app/widget.js"]')) {
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.bugdrop.app/widget.js';
    script.setAttribute('data-site-id', process.env.REACT_APP_CDN_SITE_ID as string);
    document.body.appendChild(script);
  };

  useEffect(() => {
    if (!token && !currentUser) {
      if (window.$crisp) {
        window.$crisp.push(['do', 'chat:hide']);
      }
      // unload cdn.bugdrop.app script if it exists
      const bugdropScript = document.querySelector('script[src="https://cdn.bugdrop.app/widget.js"]');
      if (bugdropScript) {
        bugdropScript.remove();
      }
    }
  }, [token, currentUser]);

  useEffect(() => {
    if (token && currentUser && currentUser?.email) {
      initializeCrisp();
      initializeOpenReplay();
      initializeCdnBug();
    }
  }, [token, currentUser?.email]);

  return <RouterProvider router={router} />;
};


const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(<Provider store={store}>
  <PersistGate loading={null} persistor={persistor}>
    {process.env.REACT_APP_MODE === 'dev' ? <RouterProvider router={router} /> : <RootComponent />}
  </PersistGate>
</Provider>);